import {
  type DemoAppointment,
  isVisitIntakeLinkLive,
  type PatientIntakeSections,
  patientIntakeAsksIdentity,
  type SchedulingWalkIn,
  type VisitIntakeLink,
} from "@emr/contracts";
import { appointmentIntakeLink, type SchedulingClientState } from "../../demo/demoSchedulingClient";
import {
  type AppointmentRowFacts,
  type AppointmentRowState,
  appointmentRowFacts,
  appointmentRowState,
} from "./appointmentRowState";

/*
 * A visit already in the clinic and its questionnaire on the patient's phone (PA-5 /
 * EXECDEV-190). Today's rows find it by encounter: the booking the visit came from, or the
 * walk-in row the desk's first code created (SchedulingStateSchema.walkIns). A walk-in without a
 * code yet has neither.
 */

export interface VisitIntake {
  /** Where the questionnaire lives: the booking the visit came from, or the walk-in's own row. */
  readonly kind: "booking" | "walk_in";
  /** The scheduling row the review routes take; absent until a walk-in's first code. */
  readonly schedulingId: string | undefined;
  /** The live desk-issued link, as last read (no token: only the issue response has it). */
  readonly link: VisitIntakeLink | undefined;
  /** The patient's answers are in. */
  readonly answered: boolean;
  /** What the one row rule reads, once the visit has a scheduling row. */
  readonly facts: AppointmentRowFacts | undefined;
}

const NO_SCHEDULING_ROW: VisitIntake = {
  kind: "walk_in",
  schedulingId: undefined,
  link: undefined,
  answered: false,
  facts: undefined,
};

/** A walk-in row for the one row rule: always on a chart, never pending before arrival. */
export function walkInRowFacts(walkIn: SchedulingWalkIn): AppointmentRowFacts {
  return {
    durable: true,
    status: walkIn.status,
    chartLinked: true,
    intakePending: false,
    intakeReview: walkIn.intakeReview,
  };
}

function bookingIntake(booking: DemoAppointment): VisitIntake {
  return {
    kind: "booking",
    schedulingId: booking.id,
    link: appointmentIntakeLink(booking),
    answered: booking.intakeStatus === "completed" || booking.intakeReview !== undefined,
    facts: appointmentRowFacts(booking),
  };
}

function walkInIntake(walkIn: SchedulingWalkIn): VisitIntake {
  return {
    kind: "walk_in",
    schedulingId: walkIn.id,
    link: walkIn.intakeLink,
    answered: walkIn.intakeStatus === "completed",
    facts: walkInRowFacts(walkIn),
  };
}

/**
 * Finds each visit's questionnaire by encounter, for a whole board at once: the booking the visit
 * came from first, then the walk-in's own row.
 */
export function visitIntakeLookup(
  state: SchedulingClientState | null | undefined,
): (encounterId: string) => VisitIntake {
  const bookings = new Map(
    (state?.appointments ?? []).flatMap((appointment) =>
      appointment.encounterId === undefined
        ? []
        : [[appointment.encounterId, appointment] as const],
    ),
  );
  const walkIns = new Map((state?.walkIns ?? []).map((walkIn) => [walkIn.encounterId, walkIn]));
  return (encounterId) => {
    const booking = bookings.get(encounterId);
    if (booking !== undefined) return bookingIntake(booking);
    const walkIn = walkIns.get(encounterId);
    return walkIn === undefined ? NO_SCHEDULING_ROW : walkInIntake(walkIn);
  };
}

export function visitIntake(
  state: SchedulingClientState | null | undefined,
  encounterId: string,
): VisitIntake {
  const booking = state?.appointments.find((candidate) => candidate.encounterId === encounterId);
  if (booking !== undefined) return bookingIntake(booking);
  const walkIn = state?.walkIns?.find((candidate) => candidate.encounterId === encounterId);
  return walkIn === undefined ? NO_SCHEDULING_ROW : walkInIntake(walkIn);
}

/** A code is out and the patient has not answered yet (the row rule's `walkInLinkWaiting`). */
export function visitIntakeWaiting(intake: VisitIntake, now: Date): boolean {
  return !intake.answered && isVisitIntakeLinkLive(intake.link, now);
}

/** The row's one pill and action (lead decision S2), with the code signal filled in. */
export function visitIntakeRowState(intake: VisitIntake, now: Date): AppointmentRowState | null {
  if (intake.facts === undefined) return null;
  return appointmentRowState(intake.facts, {
    walkInLinkWaiting: visitIntakeWaiting(intake, now),
  });
}

/** Clinical visit statuses in which the patient is in the clinic and the visit is open. */
const OPEN_VISIT: ReadonlySet<string> = new Set([
  "waiting",
  "roomed",
  "in_progress",
  "ready_for_discharge",
]);

/**
 * Whether the desk may send this visit's questionnaire to the patient's phone: the patient has
 * arrived, the visit is open, and the answers are not in yet. A booking still before check-in
 * keeps Schedule's own links.
 */
export function canSendIntakeToPhone(
  row: { readonly appointmentStatus?: string | undefined },
  intake: VisitIntake,
): boolean {
  return OPEN_VISIT.has(row.appointmentStatus ?? "") && !intake.answered;
}

/** "about you, coverage and health" or "coverage and health": what the link asks. */
export function intakeSectionsPhrase(sections: PatientIntakeSections): string {
  return patientIntakeAsksIdentity(sections)
    ? "about you, coverage and health"
    : "coverage and health";
}

/**
 * When a code stops working, on the clinic's clock: "11:59 p.m. on Thu, Oct 8". A link that runs
 * to the end of the clinic's day expires at local midnight, which reads as the last minute of
 * that day rather than "12:00 a.m." of the next.
 */
export function codeExpiryLabel(expiresAt: string, timeZone: string): string {
  const instant = Date.parse(expiresAt);
  const clock = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(instant));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    clock.find((candidate) => candidate.type === type)?.value;
  const shown = new Date(
    part("hour") === "00" && part("minute") === "00" ? instant - 60_000 : instant,
  );
  const time = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(shown);
  const day = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(shown);
  return `${time} on ${day}`;
}

/** The link as people read it: without "https://", which phones add themselves. */
export function shortPatientLink(url: string): string {
  return url.startsWith("https://") ? url.slice("https://".length) : url;
}
